// src/components/WhatsAppCampaignsDialog/WhatsAppCampaignsDialog.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Stack,
  Chip,
} from "@mui/material";

// I) Add this set of protected templates (not allowed in campaigns)
const PROTECTED_TEMPLATE_NAMES = new Set([
  "onboarding_welcome_flow",
  "utility_optout",
]);
const getTid = (t) => String(t?.template_id ?? t?.id ?? "");

// --- Scheduling helpers (UTC) ---
function toLocalDatetimeInputValueFromUtc(isoUtc) {
  if (!isoUtc) return "";
  const d = new Date(isoUtc); // interpret as UTC instant
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  const MM = pad(d.getUTCMonth() + 1);
  const dd = pad(d.getUTCDate());
  const HH = pad(d.getUTCHours());
  const mm = pad(d.getUTCMinutes());
  return `${yyyy}-${MM}-${dd}T${HH}:${mm}`;
}

function toIsoUtcFromLocalDatetimeInput(value) {
  if (!value) return null;
  return `${value}:00Z`;
}

// Enforce :00 or :30 minutes (snap to nearest slot, UTC-safe)
function snapToHalfHour(value) {
  if (!value) return "";
  const d = new Date(`${value}:00Z`); // treat input as UTC for math
  if (Number.isNaN(d.getTime())) return value;
  const m = d.getUTCMinutes();
  if (m === 0 || m === 30) return value;
  if (m < 15) d.setUTCMinutes(0, 0, 0);
  else if (m < 45) d.setUTCMinutes(30, 0, 0);
  else {
    d.setUTCHours(d.getUTCHours() + 1, 0, 0, 0);
  }
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  const MM = pad(d.getUTCMonth() + 1);
  const dd = pad(d.getUTCDate());
  const HH = pad(d.getUTCHours());
  const mm = pad(d.getUTCMinutes());
  return `${yyyy}-${MM}-${dd}T${HH}:${mm}`;
}

function isHalfHour(value) {
  if (!value) return false;
  const mm = Number(value.slice(-2));
  return mm === 0 || mm === 30;
}

export default React.memo(function WhatsAppCampaignsDialog({
  open,
  campaign = {},
  testedTemplates = [],
  usedTemplateIds = new Set(),
  onClose,
  onSave, // receives updated campaign object
}) {
  const [campaign_name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [audienceType, setAudienceType] = useState("all"); // 'all' or 'csv'
  const [csvFile, setCsvFile] = useState(null);
  const [csvNumbers, setCsvNumbers] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [scheduleMode, setScheduleMode] = useState("on_demand");
  const [scheduleAtInput, setScheduleAtInput] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(campaign.campaign_name || "");

    // If the existing campaign’s template is protected, clear it.
    // pull current template id from either campaign.template or campaign.template_id
    const incomingTemplateId = String(
      campaign?.template?.template_id ??
      campaign?.template?.id ??
      campaign?.template_id ??
      ""
    );
    // Seed value directly from the campaign
    setTemplateId(incomingTemplateId || "");

    if (campaign.audience) {
      if (campaign.audience.type === "csv") {
        setAudienceType("csv");
        setCsvNumbers(campaign.audience.raw || []);
      } else {
        setAudienceType("all");
      }
    } else {
      setAudienceType("all");
    }
     setCsvFile(null);
     setScheduleMode(campaign.schedule_mode || "on_demand");
     setScheduleAtInput(
      toLocalDatetimeInputValueFromUtc(campaign.schedule_run_at_utc || null)
    );    
  }, [open, campaign, testedTemplates]);

  // II) Only allow non-protected templates in the UI
  // Also hide templates already used by another campaign,
  // EXCEPT when editing and the current campaign already uses it.
  const currentTemplateId = String(
    campaign?.template?.template_id ??
    campaign?.template?.id ??
    campaign?.template_id ??
    ""
  );
  const isEditing = !!campaign?.campaign_id;
  // Base: tested, not protected, not used by other campaigns
  const base = (testedTemplates || []).filter((t) => {
    if (PROTECTED_TEMPLATE_NAMES.has(t.template_name)) return false;
    const tid = getTid(t);
    // allow current template if editing
    if (isEditing && String(tid) === currentTemplateId) return true;
    return !usedTemplateIds.has(String(tid));
  });

  // Ensure the currently assigned template is present as an option
  let selectableTemplates = base;
  if (isEditing && currentTemplateId) {
    const alreadyIn = base.some((t) => String(getTid(t)) === currentTemplateId);
    if (!alreadyIn) {
      // Create a lightweight option using any name we can find from the campaign
      const fallbackName =
        campaign?.template?.template_name ||
        campaign?.template_name ||            // if your backend hydrated this
        campaign?.template?.name ||
        `Template ${currentTemplateId}`;
      selectableTemplates = [
        { template_id: currentTemplateId, id: currentTemplateId, template_name: fallbackName },
        ...base,
      ];
    }
  }

  const selectedTemplate =
    selectableTemplates.find((t) => getTid(t) === String(templateId)) || null;

  const scheduleOk =
    scheduleMode === "on_demand" || (scheduleMode === "scheduled" && isHalfHour(scheduleAtInput));

  const canSave =
    campaign_name.trim() !== "" &&
    !!selectedTemplate &&
    (audienceType === "all" || (audienceType === "csv" && csvNumbers.length > 0)) &&
    scheduleOk;

  const handleCsvUpload = (file) => {
    if (!file) return;
    setCsvFile(file);
    setParsing(true);
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result;
      const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      const entries = lines
        .map((l) => {
          const parts = l.split(",").map((p) => p.trim());
          if (parts.length >= 2) {
            return (parts[0] + parts[1]).replace(/\s+/g, "");
          }
          return null;
        })
        .filter(Boolean);
      const uniqueNumbers = Array.from(new Set(entries));
      setCsvNumbers(uniqueNumbers);
      setParsing(false);
    };
    reader.onerror = () => {
      setCsvNumbers([]);
      setParsing(false);
    };
    reader.readAsText(file);
  };

  const handleSave = () => {
    let audience = null;
    if (audienceType === "csv") {
      audience = {
        type: "csv",
        raw: csvNumbers,
        count: csvNumbers.length,
        file: csvFile, // include original file for parent to encode
      };
    } else {
      audience = {
        type: "all",
        count: campaign.audience?.count || null,
      };
    }

    onSave({
      ...campaign,
      campaign_name: campaign_name.trim(),
      template: selectedTemplate, // will never be protected due to filtering
      audience,
      schedule_mode: scheduleMode,
    schedule_run_at_utc:
      scheduleMode === "scheduled"
        ? toIsoUtcFromLocalDatetimeInput(snapToHalfHour(scheduleAtInput))
        : null,
   });
  };

  if (!open) return null;

  return (
    <Dialog open fullWidth onClose={onClose}>
      <DialogTitle>
        {campaign.campaign_id ? "Edit Campaign" : "New Campaign"}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Campaign Name"
            value={campaign_name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="template-select-label">Tested Template</InputLabel>
            <Select
              labelId="template-select-label"
              value={String(templateId)}
              label="Tested Template"
              onChange={(e) => setTemplateId(String(e.target.value))}
             renderValue={(v) => {
               const id = String(v ?? "");
               // try to find a label from the options
               const match = selectableTemplates.find(t => getTid(t) === id);
               if (match?.template_name) return match.template_name;
               // fallbacks from the campaign payload so edit never shows blank
               return (
                 campaign?.template?.template_name ||
                 campaign?.template_name ||
                 (id ? `Template ${id}` : "")
               );
             }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {selectableTemplates.map((t) => {
                const tid = String(getTid(t));
                return (
                  <MenuItem key={tid} value={tid}>
                    {t.template_name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {/* Scheduling */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Audience
            </Typography>
            <RadioGroup
              row
              value={audienceType}
              onChange={(e) => {
                setAudienceType(e.target.value);
                if (e.target.value === "all") {
                  setCsvFile(null);
                  setCsvNumbers([]);
                }
              }}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label="All customers (with consent)"
              />
              <FormControlLabel
                value="csv"
                control={<Radio />}
                label="Upload CSV segmented list"
              />
            </RadioGroup>

            {audienceType === "csv" && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" gutterBottom>
                  CSV format: <code>countrycode, number</code> as headers, per line, e.g.{" "}
                  <code>44,7972101308</code>
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={parsing}
                  sx={{ mb: 1 }}
                >
                  {csvFile ? "Change CSV" : "Upload CSV"}
                  <input
                    type="file"
                    hidden
                    accept=".csv"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleCsvUpload(f);
                      setCsvFile(f);
                    }}
                  />
                </Button>
                {parsing && <Typography>Parsing CSV...</Typography>}
                {csvFile && !parsing && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>{csvFile.name}</Typography>
                    <Chip label={`${csvNumbers.length} unique`} size="small" />
                  </Stack>
                )}
                {csvNumbers.length === 0 && csvFile && !parsing && (
                  <Typography variant="body2" color="error">
                    No valid entries parsed.
                  </Typography>
                )}
              </Box>
            )}

            {audienceType === "all" && (
              <Box sx={{ mt: 1 }}>
              <Typography variant="body2" paragraph>
                Targets every customer in the database with consent.
              </Typography>
              </Box>
            )}
          </Box>
        </Box>
            <Box>
            <Typography variant="subtitle2" gutterBottom>
              Schedule
            </Typography>
            <RadioGroup
              row
              value={scheduleMode}
              onChange={(e) => setScheduleMode(e.target.value)}
            >
              <FormControlLabel value="on_demand" control={<Radio />} label="Run on demand" />
              <FormControlLabel value="scheduled" control={<Radio />} label="Run on schedule" />
            </RadioGroup>
            {scheduleMode === "scheduled" && (
              <Box sx={{ mt: 1, display: "grid", gap: 1 }}>
                <TextField
                  type="datetime-local"
                  inputProps={{ step: 1800 }}  // 30-minute increments
                  value={scheduleAtInput}
                  onChange={(e) => setScheduleAtInput(e.target.value)}
                  onBlur={(e) => setScheduleAtInput(snapToHalfHour(e.target.value))}
                  fullWidth
                  label="UTC date & time"
                  helperText={scheduleMode === "scheduled"
                    ? (isHalfHour(scheduleAtInput)
                        ? "The campaign will be sent within 5 minutes of the allotted time."
                        : "Time will round to on the hour or at :30.")
                    : ""}
                  error={scheduleMode === "scheduled" && !isHalfHour(scheduleAtInput)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
          </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={handleSave}>
          {campaign.campaign_id ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});